import { gql } from '@apollo/client'
import { useApolloClient, useMutation } from '@apollo/client/react'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import LogoutIcon from '@mui/icons-material/Logout'
import PersonIcon from '@mui/icons-material/Person'
import PlaceIcon from '@mui/icons-material/Place'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import {
  AppBar,
  Avatar,
  Box,
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { getAssetUrl, getImageUrl, isFeatureEnabled } from './config'
import { SiteRole, UserLogoutDocument } from './gql/graphql'
import type { SiteInfo } from './hooks'
import { useCurrentUser, useRole } from './hooks'
import { siteRoleLabel } from './LoginRedirector/RoleSelectionDialog'

gql`
  mutation UserLogout {
    logout {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }
`

const drawerWidth = 180
const miniDrawerWidth = 64

export interface MenuItemDef {
  text: string
  icon?: React.ReactNode
  path: string
  superAdminOnly?: boolean
  devOnly?: boolean
}

export interface MenuGroupDef {
  group: string
  icon: React.ReactNode
  children: MenuItemDef[]
  featureGate?: string
  superAdminOnly?: boolean
}

export type NavItem = MenuItemDef | MenuGroupDef

export function isGroup(item: NavItem): item is MenuGroupDef {
  return 'group' in item
}

export interface SiteSwitcher {
  sites: SiteInfo[]
  currentSite: SiteInfo | undefined
}

interface MainWindowProps {
  navItems: NavItem[]
}

export default function MainWindow({ navItems }: MainWindowProps) {
  const [currentUser] = useCurrentUser()
  const role = useRole()
  const sites = role.sites
  const currentSite = role.currentSite
  const drawerOpen = true
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>({})
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null)
  const userMenuOpen = Boolean(userMenuAnchor)
  const navigate = useNavigate()
  const location = useLocation()

  // Open the group holding the current route, e.g. on landing on a page or
  // after a redirect; other groups keep whatever state the user left them in.
  useEffect(() => {
    const activeGroup = navItems.find(
      (item) =>
        isGroup(item) && item.children.some((c) => c.path === location.pathname),
    )
    if (activeGroup && isGroup(activeGroup))
      setGroupOpen((o) =>
        o[activeGroup.group] ? o : { ...o, [activeGroup.group]: true },
      )
  }, [navItems, location.pathname])

  const [logoutMutation] = useMutation(UserLogoutDocument)
  const apolloClient = useApolloClient()

  // Calculate if user has multiple roles (app admin + site admin/coach, or
  // multiple site admin/coach roles)
  const isAppAdmin = currentUser?.isAppAdmin ?? false
  const adminSites = sites.filter(
    (s) => s.roles.includes(SiteRole.Admin) || s.roles.includes(SiteRole.SuperAdmin),
  )
  const coachSites = sites.filter((s) => s.roles.includes(SiteRole.Coach))
  const roleCount = (isAppAdmin ? 1 : 0) + adminSites.length + coachSites.length
  const hasMultipleRoles = roleCount > 1

  const isDevParam = new URLSearchParams(location.search).has('dev')

  // Filter nav items, applying superAdminOnly and devOnly rules to leaf items
  const visibleNavItems = useMemo(() => {
    const filterItem = (item: MenuItemDef) => {
      if (item.superAdminOnly && !currentSite?.roles.includes(SiteRole.SuperAdmin))
        return false
      if (item.devOnly && !isDevParam) return false
      return true
    }
    return navItems
      .map((item) => {
        if (isGroup(item)) {
          if (item.featureGate && !isFeatureEnabled(item.featureGate)) return null
          if (item.superAdminOnly && !currentSite?.roles.includes(SiteRole.SuperAdmin))
            return null
          const children = item.children.filter(filterItem)
          return children.length > 0 ? { ...item, children } : null
        }
        return filterItem(item) ? item : null
      })
      .filter(Boolean) as NavItem[]
  }, [navItems, currentSite?.roles, isDevParam])

  const handleMenuItemClick = (path?: string, event?: React.MouseEvent) => {
    // Blur the button to prevent focus conflicts with dialogs
    if (event?.currentTarget) {
      ;(event.currentTarget as HTMLElement).blur()
    }

    if (path) {
      navigate(path)
    }
  }

  const handleLogout = async () => {
    setUserMenuAnchor(null) // Close menu

    try {
      const { data } = await logoutMutation()

      if (data?.logout.__typename === 'GraphqlRequestError') {
        console.error('Logout failed:', data.logout.message)
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Always clear local state regardless of mutation result
      localStorage.removeItem('authToken')
      await apolloClient.clearStore()
      // Force page reload to trigger LoginRedirector re-authentication check
      window.location.href = '/'
    }
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          {/* Logo/Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={getAssetUrl('/logo.svg')} alt="Logo" style={{ height: 32 }} />
            <Typography variant="h6" component="div">
              几点几体育
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />

          {/* Right side - Site name display and User */}
          {currentSite && role.selectedRole.type !== 'app_admin' && (
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <Avatar
                sx={{ width: 32, height: 32, mr: 0.5 }}
                src={currentSite.avatar ? getImageUrl(currentSite.avatar) : undefined}
              >
                {!currentSite.avatar && <PlaceIcon />}
              </Avatar>
              <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                <Typography variant="body2" data-testid="site-name-display">
                  {currentSite.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.8 }}
                  data-testid="site-role-display"
                >
                  {siteRoleLabel(role.selectedRole.type, currentSite)}
                </Typography>
              </Box>
            </Box>
          )}

          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            aria-controls={userMenuOpen ? 'user-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={userMenuOpen ? 'true' : undefined}
          >
            <Avatar
              sx={{ width: 32, height: 32 }}
              src={currentUser?.avatar ? getImageUrl(currentUser.avatar) : undefined}
            >
              {!currentUser?.avatar && <PersonIcon />}
            </Avatar>
            <Typography variant="body2">{currentUser?.name}</Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        id="user-menu"
        anchorEl={userMenuAnchor}
        open={userMenuOpen}
        onClose={() => setUserMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {hasMultipleRoles && (
          <MenuItem
            onClick={() => {
              setUserMenuAnchor(null)
              role.switchRole()
            }}
          >
            <ListItemIcon>
              <SwapHorizIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>切换身份</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>退出登录</ListItemText>
        </MenuItem>
      </Menu>

      <Drawer
        variant="permanent"
        open={drawerOpen}
        sx={{
          width: drawerOpen ? drawerWidth : miniDrawerWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          '& .MuiDrawer-paper': {
            width: drawerOpen ? drawerWidth : miniDrawerWidth,
            boxSizing: 'border-box',
            mt: 8,
            height: 'calc(100% - 64px)',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
          },
        }}
      >
        <List sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          {visibleNavItems
            .filter(
              (item) =>
                (!isGroup(item) && (item as MenuItemDef).devOnly !== true) ||
                isGroup(item),
            )
            .map((item) => {
              if (isGroup(item)) {
                const isChildSelected = item.children.some(
                  (c) => location.pathname === c.path,
                )
                return (
                  <Box key={item.group}>
                    <ListItem disablePadding sx={{ display: 'block' }}>
                      <ListItemButton
                        onClick={() =>
                          setGroupOpen((o) => ({ ...o, [item.group]: !o[item.group] }))
                        }
                        selected={isChildSelected && !groupOpen[item.group]}
                        sx={{
                          minHeight: 48,
                          justifyContent: drawerOpen ? 'initial' : 'center',
                          px: 2.5,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: drawerOpen ? 1.5 : 'auto',
                            justifyContent: 'center',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.group}
                          sx={{ opacity: drawerOpen ? 1 : 0 }}
                        />
                        {drawerOpen &&
                          (groupOpen[item.group] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ChevronRightIcon />
                          ))}
                      </ListItemButton>
                    </ListItem>
                    <Collapse
                      in={groupOpen[item.group] && drawerOpen}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List disablePadding>
                        {item.children.map((child) => {
                          const isSelected = location.pathname === child.path
                          return (
                            <ListItem
                              key={child.text}
                              disablePadding
                              sx={{ display: 'block' }}
                            >
                              <ListItemButton
                                onClick={(e) => handleMenuItemClick(child.path, e)}
                                selected={isSelected}
                                sx={{ minHeight: 40, pl: 4 }}
                              >
                                <ListItemText primary={child.text} />
                              </ListItemButton>
                            </ListItem>
                          )
                        })}
                      </List>
                    </Collapse>
                  </Box>
                )
              }

              const flatItem = item as MenuItemDef
              const isSelected = location.pathname === flatItem.path

              return (
                <ListItem key={flatItem.text} disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    onClick={(e) => handleMenuItemClick(flatItem.path, e)}
                    selected={isSelected}
                    sx={{
                      minHeight: 48,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: drawerOpen ? 1.5 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {flatItem.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={flatItem.text}
                      sx={{ opacity: drawerOpen ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            })}
          <Box sx={{ flexGrow: 1 }} />
          {visibleNavItems
            .filter((item) => !isGroup(item) && (item as MenuItemDef).devOnly === true)
            .map((item) => {
              const flatItem = item as MenuItemDef
              const isSelected = location.pathname === flatItem.path
              return (
                <ListItem key={flatItem.text} disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    onClick={(e) => handleMenuItemClick(flatItem.path, e)}
                    selected={isSelected}
                    sx={{
                      minHeight: 48,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: drawerOpen ? 1.5 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {flatItem.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={flatItem.text}
                      sx={{ opacity: drawerOpen ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            })}
        </List>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          transition: (theme) =>
            theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        <Outlet />
      </Box>
    </Box>
  )
}
